import { apiCallWithAuth } from "../helper/apiManager";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import {
  ICompteur,
  ICompteurFilter,
  ICompteurRaz,
  ICompteurRazRequest,
  ICompteurRazStep,
  ICompteurSchemaPref,
} from "../models/compteur";
import { API_CENTRE } from "./config";

const BASE_URL = API_CENTRE + "/api/v1/compteurs";

// Lot9 / E30 — Service des compteurs (read-model).
// Lecture : la table est alimentée par projection serveur (in-process).
// Écriture limitée à RAZ/rebuild (U03) : recalcul déterministe, pas de saisie directe.
export default class CompteurService {
  static async search(
    filter?: ICompteurFilter,
    centerId?: number
  ): Promise<ServiceOperationResult<ICompteur[]>> {
    try {
      const params = new URLSearchParams();
      if (filter?.date) params.set("date", filter.date);
      if (centerId != null) params.set("centerId", String(centerId));
      const query = params.toString();
      const url = query ? `${BASE_URL}?${query}` : BASE_URL;
      const result = await apiCallWithAuth(url, "get");
      return new ServiceOperationResult<ICompteur[]>(false, result.data);
    } catch (err: any) {
      return new ServiceOperationResult<ICompteur[]>(true, err);
    }
  }

  /**
   * U03 — Recalcul anti-dérive des compteurs Besoin/Dispo/Attente/Parc/Stock du jour
   * pour le centre courant (reprojette depuis les données métier existantes).
   * Ne touche PAS au Baseline (« Prévu »), qui est un snapshot posé au RAZ.
   * @returns nombre de lignes recalculées.
   */
  static async rebuild(
    date?: string,
    centerId?: number
  ): Promise<ServiceOperationResult<number>> {
    try {
      const params = new URLSearchParams();
      if (date) params.set("date", date);
      if (centerId != null) params.set("centerId", String(centerId));
      const query = params.toString();
      const url = query ? `${BASE_URL}/rebuild?${query}` : `${BASE_URL}/rebuild`;
      const result = await apiCallWithAuth(url, "post");
      return new ServiceOperationResult<number>(false, result.data?.rebuilt ?? 0);
    } catch (err: any) {
      return new ServiceOperationResult<number>(true, err);
    }
  }

  /**
   * U03 — RAZ des compteurs : enregistre la date de référence + le plan de monte choisi et lance le
   * recalcul (6 étapes, une par Nature) en tâche de fond côté serveur. La réponse revient tout de
   * suite avec les étapes à "Pending" — l'avancement est ensuite poussé en temps réel (SignalR
   * "MajCompteurRazStep"), pas d'attente bloquante du recalcul complet.
   */
  static async raz(request: ICompteurRazRequest): Promise<ServiceOperationResult<ICompteurRaz>> {
    try {
      const result = await apiCallWithAuth(BASE_URL + "/raz", "post", request);
      return new ServiceOperationResult<ICompteurRaz>(false, result.data);
    } catch (err: any) {
      return new ServiceOperationResult<ICompteurRaz>(true, err);
    }
  }

  /** U03 — Étapes d'un RAZ (secours si la notification temps réel a été manquée, ex. reconnexion). */
  static async getRazSteps(razId: number): Promise<ServiceOperationResult<ICompteurRazStep[]>> {
    try {
      const result = await apiCallWithAuth(`${BASE_URL}/raz/${razId}/steps`, "get");
      return new ServiceOperationResult<ICompteurRazStep[]>(false, result.data ?? []);
    } catch (err: any) {
      return new ServiceOperationResult<ICompteurRazStep[]>(true, err);
    }
  }

  /**
   * U03 — Dernier RAZ enregistré pour le jour (et donc le plan de monte actuellement associé
   * au « Prévu » affiché), pour le centre courant. Null si aucun RAZ n'a encore été fait ce jour-là.
   */
  static async getLatestRaz(
    date?: string,
    centerId?: number
  ): Promise<ServiceOperationResult<ICompteurRaz | null>> {
    try {
      const params = new URLSearchParams();
      if (date) params.set("date", date);
      if (centerId != null) params.set("centerId", String(centerId));
      const query = params.toString();
      const url = query ? `${BASE_URL}/raz?${query}` : `${BASE_URL}/raz`;
      const result = await apiCallWithAuth(url, "get");
      return new ServiceOperationResult<ICompteurRaz | null>(false, result.data ?? null);
    } catch (err: any) {
      return new ServiceOperationResult<ICompteurRaz | null>(true, err);
    }
  }

  /** U02 — Préférences d'affichage des schémas (sélection + ordre). Centre fourni par le front. */
  static async getSchemaPrefs(centerId?: number): Promise<ServiceOperationResult<ICompteurSchemaPref[]>> {
    try {
      let url = BASE_URL + "/schema-prefs";
      if (centerId != null) url += `?centerId=${centerId}`;
      const result = await apiCallWithAuth(url, "get");
      return new ServiceOperationResult<ICompteurSchemaPref[]>(false, result.data);
    } catch (err: any) {
      return new ServiceOperationResult<ICompteurSchemaPref[]>(true, err);
    }
  }

  /** U02 — Remplace TOUTES les préférences de schémas du centre (sélection + ordre). */
  static async saveSchemaPrefs(
    prefs: ICompteurSchemaPref[],
    centerId?: number
  ): Promise<ServiceOperationResult<ICompteurSchemaPref[]>> {
    try {
      let url = BASE_URL + "/schema-prefs";
      if (centerId != null) url += `?centerId=${centerId}`;
      const result = await apiCallWithAuth(url, "put", prefs);
      return new ServiceOperationResult<ICompteurSchemaPref[]>(false, result.data);
    } catch (err: any) {
      return new ServiceOperationResult<ICompteurSchemaPref[]>(true, err);
    }
  }

  /**
   * U05 — Potentiel doses d'un verrat (REQ896 / D5) : restant à orienter,
   * pour le centre fourni par le front. @returns nombre de doses.
   */
  static async getBoarPotential(
    boarId: number,
    centerId?: number
  ): Promise<ServiceOperationResult<number>> {
    try {
      const params = new URLSearchParams({ boarId: String(boarId) });
      if (centerId != null) params.set("centerId", String(centerId));
      const result = await apiCallWithAuth(`${BASE_URL}/boar-potential?${params.toString()}`, "get");
      return new ServiceOperationResult<number>(false, result.data?.potential ?? 0);
    } catch (err: any) {
      return new ServiceOperationResult<number>(true, err);
    }
  }
}
